import { useState } from "react";
import { trackItems, type TrackItem } from "@/mocks/home";
import SectionHead from "./SectionHead";

const FILTERS: { key: string; label: string }[] = [
  { key: "all", label: "全部" },
  { key: "heat", label: "追踪中" },
  { key: "turning", label: "反转中" },
  { key: "responded", label: "已回应" },
  { key: "cleared", label: "已澄清" },
  { key: "checking", label: "待核实" },
];

const STATUS: Record<TrackItem["status"], { label: string; cls: string }> = {
  heat: { label: "追踪中", cls: "bg-primary-500/15 text-primary-300 border-primary-500/30" },
  turning: { label: "反转中", cls: "bg-accent-500 text-background-950 border-accent-500" },
  responded: { label: "已回应", cls: "bg-accent-500/15 text-accent-300 border-accent-500/30" },
  cleared: { label: "已澄清", cls: "bg-secondary-500/15 text-secondary-300 border-secondary-500/30" },
  checking: { label: "待核实", cls: "bg-background-300/50 text-foreground-500 border-background-300/60" },
};

function Tag({ children }: { children: string }) {
  return (
    <span className="whitespace-nowrap rounded-full bg-background-300/40 px-2 py-0.5 text-[11px] text-foreground-600">
      {children}
    </span>
  );
}

function SmallCard({ item }: { item: TrackItem }) {
  const s = STATUS[item.status];
  return (
    <article className="group flex flex-col justify-between gap-3 rounded-lg border border-background-200/70 bg-background-100/50 p-4 transition hover:border-background-300/70 hover:bg-background-100">
      <div>
        <div className="flex items-center gap-2">
          {item.hot && (
            <span className="flex items-center gap-1 rounded-full bg-primary-500/15 px-2 py-0.5 text-[11px] font-semibold text-primary-300">
              <i className="ri-fire-fill" /> 热
            </span>
          )}
          <span className={`inline-flex whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] ${s.cls}`}>
            {s.label}
          </span>
        </div>
        <h3 className="mt-3 font-heading text-[15px] font-bold leading-snug text-foreground-950">{item.title}</h3>
        <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-foreground-600">{item.summary}</p>
      </div>
      <div className="flex flex-wrap items-center gap-1.5 pt-1">
        {item.tags.map((t) => (
          <Tag key={t}>{t}</Tag>
        ))}
        <div className="ml-auto flex shrink-0 items-center gap-3 text-[11px] text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-eye-line" />
            {item.views}
          </span>
          <span>{item.time}</span>
        </div>
      </div>
    </article>
  );
}

function FeaturedCard({ item }: { item: TrackItem }) {
  const s = STATUS[item.status];
  return (
    <article className="group relative overflow-hidden rounded-lg border border-background-200/70 bg-background-100 p-5 md:p-6">
      <span className="pointer-events-none absolute -right-10 -top-12 h-44 w-44 rounded-full bg-primary-500/10 blur-2xl" />
      <div className="relative flex flex-col gap-5 lg:flex-row lg:items-center">
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="flex items-center gap-1 rounded-full bg-primary-500 px-2.5 py-1 text-[11px] font-semibold text-background-50">
              <i className="ri-star-fill" /> 焦点追踪
            </span>
            <span className={`inline-flex whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] ${s.cls}`}>
              {s.label}
            </span>
            <span className="text-[11px] text-foreground-500">{item.time}</span>
          </div>
          <h3 className="mt-4 font-heading text-xl font-black leading-snug text-foreground-950 md:text-2xl">
            {item.title}
          </h3>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">{item.summary}</p>
          <div className="mt-4 flex flex-wrap items-center gap-1.5">
            {item.tags.map((t) => (
              <Tag key={t}>{t}</Tag>
            ))}
          </div>
        </div>
        <div className="flex flex-row gap-4 lg:w-44 lg:flex-col lg:items-stretch">
          <div className="flex-1 rounded-lg bg-background-200/50 p-3 text-center">
            <div className="font-heading text-2xl font-black text-accent-400">48h</div>
            <div className="mt-0.5 text-[11px] text-foreground-600">发酵至澄清窗口</div>
          </div>
          <div className="flex-1 rounded-lg bg-background-200/50 p-3 text-center">
            <div className="font-heading text-2xl font-black text-secondary-400">3</div>
            <div className="mt-0.5 text-[11px] text-foreground-600">版口径对照</div>
          </div>
        </div>
      </div>
    </article>
  );
}

export default function TrackingSection() {
  const [active, setActive] = useState("all");
  const filtered = active === "all" ? trackItems : trackItems.filter((t) => t.status === active);
  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <section id="tracking" className="mx-auto w-full max-w-[1400px] px-4 py-10 md:px-6 md:py-14">
      <SectionHead
        eyebrow="事件追踪图鉴"
        title="一口看完热点事件的当前状态"
        desc="按热度和核实进展归类，把正在追踪、等待回应与已经澄清的快手黑料事件排在一起，随时查看每件事走到哪一步。"
        right={
          <div className="no-scrollbar flex flex-wrap gap-1.5">
            {FILTERS.map((f) => {
              const count =
                f.key === "all"
                  ? trackItems.length
                  : trackItems.filter((t) => t.status === f.key).length;
              const on = active === f.key;
              return (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setActive(f.key)}
                  className={`flex items-center gap-1 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition ${
                    on
                      ? "bg-accent-500 text-background-950"
                      : "bg-background-100 text-foreground-600 hover:text-foreground-900"
                  }`}
                >
                  {f.label}
                  <span className={`text-[11px] ${on ? "text-background-950/70" : "text-foreground-500"}`}>{count}</span>
                </button>
              );
            })}
          </div>
        }
      />

      <div className="mt-7 grid grid-cols-1 gap-4 lg:grid-cols-3">
        {featured && (
          <div className="lg:col-span-3">
            <FeaturedCard item={featured} />
          </div>
        )}
        {rest.map((item) => (
          <SmallCard key={item.id} item={item} />
        ))}
      </div>
    </section>
  );
}